import React, { useRef, useState, useEffect, useCallback } from 'react'
import { logger } from 'helpers/logger'
import { useAdminPermissions, useAppDispatch, useAppSelector, useDocumentationLink, useLocalStorage } from 'app/hooks'
import { Input, Button } from 'components'
import {
    hideDocumentationDialog,
    hideSnapDialog,
    toggleSnapDialogCollapse,
    updateDocumentationDialog
} from 'actions/appActions'
import { upsertDocumentationLink } from 'actions/adminActions'
import { UserRole } from 'app/types'

interface Position {
    left: number
    top: number
}

interface Size {
    width: number
    height: number
}

interface DocumentLinkForm {
    title: string
    url: string
}

// Snap positions enum
enum SnapPosition {
    NONE = 'none',
    LEFT = 'left',
    RIGHT = 'right'
}

export function DocumentationDialog() {
    const dispatch = useAppDispatch()
    const documentationDialog = useAppSelector(state => state.app.documentationDialog)
    const getDocLink = useDocumentationLink()
    const docLink = documentationDialog?.docLink || ''
    const targetId = documentationDialog?.targetId || null
    const documentation = getDocLink(docLink)
    const project = useAppSelector(state => state.app.projectCurrent[0])
    const organization = useAppSelector(state => state.app.organization)

    // Refs
    const dialogRef = useRef<HTMLDivElement>(null)
    const hasInitializedFromTargetRef = useRef<boolean>(false)

    // Local storage for persistent size and position settings
    const [storedSize, setStoredSize] = useLocalStorage('documentation-dialog-size', { width: 800, height: 600 })
    const [storedPosition, setStoredPosition] = useLocalStorage('documentation-dialog-position', { left: 100, top: 100 })

    // Local state for position, size, and dragging
    const [position, setPosition] = useState<Position>(storedPosition)
    const [size, setSize] = useState<Size>(storedSize)
    const [isDragging, setIsDragging] = useState(false)
    const [isResizing, setIsResizing] = useState(false)
    const [isSnapped, setIsSnapped] = useState(false)
    const [snapPosition, setSnapPosition] = useState<SnapPosition>(SnapPosition.NONE)
    const [originalSize, setOriginalSize] = useState<Size>(storedSize)
    const [originalPosition, setOriginalPosition] = useState<Position>(storedPosition)
    const [isVisible, setIsVisible] = useState(true)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [title, setTitle] = useState<string>(documentation?.title || 'Documentation')
    const [iframeUrl, setIframeUrl] = useState<string>(documentation?.url || '')

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [formData, setFormData] = useState<DocumentLinkForm>({
        title: documentation?.title || 'Documentation',
        url: documentation?.url || ''
    })
    const iFrameRef = useRef<HTMLIFrameElement>(null)

    // Drag state refs (to avoid issues with stale closures)
    const dragOffsetRef = useRef<Position>({ left: 0, top: 0 })
    const isDraggingRef = useRef<boolean>(false)
    const isResizingRef = useRef<boolean>(false)

    const adminPermissions = useAdminPermissions()
    const editAdmin = adminPermissions(project, organization, [UserRole.DocumentationEdit])

    // Constants
    const SNAP_THRESHOLD = 50 // Increased threshold for better snap detection
    const SNAP_ANIMATION_DURATION = 200

    // Load documentation link if provided
    useEffect(() => {
        const loadDocumentation = async () => {
            if (documentation) {
                setIframeUrl(documentation.url)
                setTitle(documentation.title || 'Documentation')
                // Initialize form data
                setFormData({
                    title: documentation.title || 'Documentation',
                    url: documentation.url
                })
            } else {
                setIframeUrl(null)
                setTitle('Documentation')
                setFormData({ title: null, url: null })
            }
            setIsVisible(true)
        }

        loadDocumentation()
    }, [docLink, documentation?.url, documentation?.title, dispatch])

    // Restore from snap
    const restoreFromSnap = useCallback(() => {
        if (!isSnapped) return

        setPosition(originalPosition)
        setSize(originalSize)
        setIsSnapped(false)
        setSnapPosition(SnapPosition.NONE)
    }, [isSnapped, originalPosition, originalSize])

    // Apply snap
    const applySnap = useCallback((snapPos: SnapPosition) => {
        // Store current position and size for restoration
        if (!isSnapped) {
            setOriginalPosition({ left: position.left, top: position.top })
            setOriginalSize({ width: size.width, height: size.height })
        }

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        // Calculate dimensions for 50% screen width
        const halfWidth = Math.floor(viewportWidth / 2)
        const snapWidth = halfWidth
        const snapHeight = viewportHeight

        if (snapPos === SnapPosition.LEFT) {
            setPosition({ left: 0, top: 0 })
            setSize({ width: snapWidth, height: snapHeight })
            setSnapPosition(SnapPosition.LEFT)
        } else if (snapPos === SnapPosition.RIGHT) {
            setPosition({ left: halfWidth, top: 0 })
            setSize({ width: snapWidth, height: snapHeight })
            setSnapPosition(SnapPosition.RIGHT)
        }

        setIsSnapped(true)
    }, [isSnapped, position.left, position.top, size.width, size.height])

    // Check for snap
    const checkForSnap = useCallback((mouseX: number, mouseY: number) => {
        const viewportWidth = window.innerWidth

        // Check for left side snap
        if (mouseX < SNAP_THRESHOLD) {
            applySnap(SnapPosition.LEFT)
            return true
        }

        // Check for right side snap
        if (mouseX > viewportWidth - SNAP_THRESHOLD) {
            applySnap(SnapPosition.RIGHT)
            return true
        }

        return false
    }, [applySnap, SNAP_THRESHOLD])

    // Mouse down handler for starting drag
    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // Don't drag when clicking on control buttons or when in edit mode
        if (
            isEditing ||
            (e.target instanceof HTMLElement &&
                (e.target.closest('.documentation-dialog-close') ||
                    e.target.closest('.documentation-dialog-collapse') ||
                    e.target.closest('.documentation-dialog-expand') ||
                    e.target.closest('.documentation-dialog-edit')))
        ) {
            return
        }

        // Prevent default to avoid text selection during drag
        e.preventDefault()

        // Calculate drag offset
        const dialogRect = dialogRef.current?.getBoundingClientRect()
        if (!dialogRect) return

        // Store offset in ref to avoid stale closures
        dragOffsetRef.current = {
            left: e.clientX - dialogRect.left,
            top: e.clientY - dialogRect.top
        }

        // Update dragging state
        isDraggingRef.current = true
        setIsDragging(true)

        // Create overlay element for mouse capture
        const overlay = document.createElement('div')
        overlay.id = 'dialog-drag-overlay'
        overlay.style.position = 'fixed'
        overlay.style.top = '0'
        overlay.style.left = '0'
        overlay.style.width = '100vw'
        overlay.style.height = '100vh'
        overlay.style.zIndex = '999999'
        overlay.style.cursor = 'grabbing'
        document.body.appendChild(overlay)

        // Define mousemove handler
        const handleMouseMove = (moveEvent: MouseEvent) => {
            moveEvent.preventDefault()

            if (!isDraggingRef.current) return

            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight

            // If dialog was previously snapped and is now being dragged again,
            // restore original dimensions and exit snap mode after moving a certain threshold
            if (isSnapped) {
                const moveThreshold = 20
                const distanceMoved = Math.sqrt(
                    Math.pow(moveEvent.clientX - (position.left + dragOffsetRef.current.left), 2) +
                    Math.pow(moveEvent.clientY - (position.top + dragOffsetRef.current.top), 2)
                )

                if (distanceMoved > moveThreshold) {
                    restoreFromSnap()
                } else {
                    // Still in snap threshold, don't move yet
                    return
                }
            }

            // Calculate new position based on mouse position and original offset
            let newLeft = moveEvent.clientX - dragOffsetRef.current.left
            let newTop = moveEvent.clientY - dragOffsetRef.current.top

            // Keep dialog within viewport bounds
            const { width, height } = dialogRef.current.getBoundingClientRect()
            newLeft = Math.max(0, Math.min(newLeft, viewportWidth - width))
            newTop = Math.max(0, Math.min(newTop, viewportHeight - height))

            // Update position
            setPosition({ left: newLeft, top: newTop })
        }

        // Define mouseup handler
        const handleMouseUp = (upEvent: MouseEvent) => {
            // Check for snap before ending the drag
            const didSnap = checkForSnap(upEvent.clientX, upEvent.clientY)

            // Update dragging state
            isDraggingRef.current = false
            setIsDragging(false)

            // If not snapped, store position for future snap restoration
            if (!didSnap && !isSnapped) {
                setOriginalPosition({ left: position.left, top: position.top })
                setOriginalSize({ width: size.width, height: size.height })
            }

            // Remove overlay and event listeners
            document.getElementById('dialog-drag-overlay')?.remove()
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }

        // Add event listeners
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
    }, [isSnapped, position.left, position.top, restoreFromSnap, size.height, size.width, checkForSnap, isEditing])

    // Clean up any lingering event listeners on unmount
    useEffect(() => {
        return () => {
            document.getElementById('dialog-drag-overlay')?.remove()
            isDraggingRef.current = false
        }
    }, [])

    // Handle form field changes
    const handleFormChange = useCallback((name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }, [])

    // Save documentation link changes
    const handleSaveDocLink = useCallback(async () => {
        if (!documentationDialog || !editAdmin) return

        setIsSaving(true)

        try {
            // Update the dialog in Redux store
            dispatch(upsertDocumentationLink({
                link: docLink,
                title: formData.title,
                url: formData.url
            }))
            // Exit edit mode
            setIsEditing(false)

            logger.info(`Documentation link updated: ${docLink}`)
        } catch (error) {
            logger.error('Error updating documentation link:', error)
        } finally {
            setIsSaving(false)
        }
    }, [dispatch, docLink, documentationDialog, formData])

    // Toggle edit mode
    const handleToggleEditMode = useCallback(() => {
        if (!editAdmin) return
        setIsEditing(prev => !prev)
    }, [editAdmin])

    // Handle close button click
    const handleCloseClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsVisible(false)
        setIframeUrl(null)
        dispatch(hideDocumentationDialog())
    }, [dispatch])

    // Handle collapse button click
    const handleCollapseClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsCollapsed(!isCollapsed)
    }, [isCollapsed])

    // Handle expand button click
    const handleExpandClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (iframeUrl) {
            window.open(iframeUrl, '_blank')
        }
    }, [iframeUrl])

    // Handle cancel edit
    const handleCancelEdit = useCallback(() => {
        // Reset form data to current values
        setFormData({
            title: title,
            url: iframeUrl
        })
        setIsEditing(false)
    }, [title, iframeUrl])

    // Initialize position and size from targetId element if provided
    useEffect(() => {
        if (targetId && !hasInitializedFromTargetRef.current) {
            const targetElement = document.getElementById(targetId)
            if (targetElement) {
                const targetRect = targetElement.getBoundingClientRect()

                // Get the stored size and position from localStorage
                const hasStoredSize = localStorage.getItem('documentation-dialog-size') !== null
                const hasStoredPosition = localStorage.getItem('documentation-dialog-position') !== null

                // Only use target dimensions if we don't have stored values
                if (!hasStoredSize || !hasStoredPosition) {
                    // Calculate position - place dialog below the target with a small gap
                    const padding = 10
                    const newPosition = {
                        left: targetRect.left + padding,
                        top: targetRect.top + padding,
                    }

                    // Calculate size - match target width but use appropriate height
                    // (use target height or sensible minimum, whichever is larger)
                    const newSize = {
                        width: Math.max(600, targetRect.width) - padding * 2,
                        height: Math.max(400, targetRect.height) - padding * 2,
                    }

                    // Update component state
                    setPosition(newPosition)
                    setSize(newSize)
                    setOriginalPosition(newPosition)
                    setOriginalSize(newSize)

                    // Update localStorage
                    setStoredPosition(newPosition)
                    setStoredSize(newSize)
                }
            }
            hasInitializedFromTargetRef.current = true

        }
    }, [targetId, setStoredPosition, setStoredSize])

    // Track CSS resize operation and store new size in localStorage
    useEffect(() => {
        if (!dialogRef.current) return
        
        let resizeTimeout: NodeJS.Timeout | null = null
        let lastRecordedSize = { width: size.width, height: size.height }
        let isAdjusting = false
        
        // Create ResizeObserver to detect when the dialog is resized using CSS resize
        const resizeObserver = new ResizeObserver(() => {
            // Skip if we're currently manually dragging, resizing, or already adjusting size
            // if (isDraggingRef.current || isResizingRef.current || isAdjusting) return
            
            // Clear any existing timeout
            if (resizeTimeout) {
                clearTimeout(resizeTimeout)
            }
            
            // Set a timeout to update state after resize completes
            resizeTimeout = setTimeout(() => {
                if (!dialogRef.current) return
                
                // Get the final dimensions
                const rect = dialogRef.current.getBoundingClientRect()
                const newWidth = Math.max(600, Math.round(rect.width))
                const newHeight = Math.max(400, Math.round(rect.height))
                
                // Use a larger threshold to prevent incremental growth
                // Only update if dimensions have changed significantly (5px or more)
                if (Math.abs(newWidth - lastRecordedSize.width) >= 5 || 
                    Math.abs(newHeight - lastRecordedSize.height) >= 5) {
                    
                    // Set flag to prevent recursive observer calls
                    isAdjusting = true
                    
                    // Update last recorded size to track actual changes
                    lastRecordedSize = { width: newWidth, height: newHeight }
                    
                    // Update state and localStorage
                    setSize({ width: newWidth, height: newHeight })
                    setStoredSize({ width: newWidth, height: newHeight })
                    
                    // Reset adjustment flag after DOM has updated
                    setTimeout(() => {
                        isAdjusting = false
                    }, 50)
                }
            }, 250) // Longer delay for more stability
        })
        
        // Start observing
        resizeObserver.observe(dialogRef.current)
        
        // Clean up
        return () => {
            resizeObserver.disconnect()
            if (resizeTimeout) {
                clearTimeout(resizeTimeout)
            }
        }
    }, [setStoredSize])

    if (!isVisible || !documentationDialog) {
        return null
    }

    const dialogStyle = {
        left: `${position.left}px`,
        top: `${position.top}px`,
        width: `${size.width}px`,
        height: isCollapsed ? 'auto' : `${size.height}px`,
        // transition: isDragging ? 'none' : `all ${SNAP_ANIMATION_DURATION}ms ease`,
    }
    return (
        <div
            ref={dialogRef}
            className={`documentation-dialog ${isCollapsed ? 'collapsed' : ''} ${isDragging ? 'active-drag' : ''} ${isSnapped ? 'snapped' : ''} ${snapPosition !== SnapPosition.NONE ? `snap-${snapPosition}` : ''} ${isEditing ? 'editing' : ''}`}
            style={dialogStyle}
        >
            <div
                className="documentation-dialog-header"
                onMouseDown={handleMouseDown}
                style={{ cursor: isDragging ? 'grabbing' : (isEditing ? 'default' : 'grab') }}
            >
                <div className="documentation-dialog-title">
                    {title}
                </div>
                <div className="documentation-dialog-controls">
                    {editAdmin && <div
                        className="documentation-dialog-edit"
                        onClick={handleToggleEditMode}
                    >
                        <i className={`fas fa-${isEditing ? 'times' : 'pen'}`}></i>
                    </div>}
                    <div
                        className="documentation-dialog-expand"
                        onClick={handleExpandClick}
                    >
                        <i className="fas fa-expand"></i>
                    </div>
                    <div
                        className="documentation-dialog-collapse"
                        onClick={handleCollapseClick}
                    >
                        <i className={`fas fa-${isCollapsed ? 'plus' : 'minus'}`}></i>
                    </div>
                    <div
                        className="documentation-dialog-close"
                        onClick={handleCloseClick}
                    >
                        <i className="fas fa-times"></i>
                    </div>
                </div>
            </div>

            {/* Edit Form */}
            {(isEditing || iframeUrl == null || iframeUrl == '') && (
                <div className="documentation-dialog-edit-form">
                    <div className="form-row">
                        <label htmlFor="doc-link">ID</label>
                        <h5 id="doc-link">{docLink}</h5>
                    </div>
                    <div className="form-row">
                        <label htmlFor="doc-title">Title</label>
                        <Input
                            id="doc-title"
                            name="title"
                            value={formData.title}
                            onChange={(x) => handleFormChange("title", x)}
                            placeholder="Documentation Title"
                            required
                        />
                    </div>
                    <div className="form-row">
                        <label htmlFor="doc-url">URL</label>
                        <Input
                            id="doc-url"
                            name="url"
                            value={formData.url}
                            onChange={(x) => handleFormChange("url", x)}
                            placeholder="Enter url"
                            required
                        />
                    </div>
                    <div className="form-actions">
                        <Button
                            onClick={handleSaveDocLink}
                            disabled={isSaving || !formData.title || !formData.url}
                            secondary
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}


            <div
                className="documentation-dialog-content"
                style={{ display: isCollapsed ? 'none' : 'block' }}>
                {!iframeUrl && <h3>Set documentation URL</h3>}
                {iframeUrl && <iframe
                    src={iframeUrl}
                    title={title}
                    ref={iFrameRef}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowFullScreen={true}
                />}
            </div>
        </div>
    )
}